import { useEffect, useState } from 'react';
import { Input, Modal, Select, notification } from 'antd';
import moment from 'moment-timezone';
import APIClient from '../api/api';
import { RenewalPreview } from '../api/models';
import { Button, Callout, Column, DataTable, StatusTag } from './ui';
import {
  RENEWAL_CONFIRM_WORD,
  RENEWAL_YEAR_OPTIONS,
  actionLabel,
  actionTone,
  canStartRenewal,
  compositionSentence,
  duplicateDomains,
  estimatedNewExpiry,
  excludedPremiumSentence,
  formatCents,
  parseDomainList,
  pricingBasisSentence,
  shortfallCents,
} from './BulkRenewal.model';

export interface Props {
  onClose: () => void;
  /** Called with the new job id once a run has started. */
  onStarted: (jobId: number) => void;
  /** Pre-fills the paste box, e.g. from a selection. */
  initialDomains?: string;
}

type State =
  | { kind: 'compose' }
  | { kind: 'previewing' }
  | { kind: 'preview'; preview: RenewalPreview }
  | { kind: 'starting'; preview: RenewalPreview };

const BulkRenewalModal = ({ onClose, onStarted, initialDomains = '' }: Props) => {
  const [state, setState] = useState<State>({ kind: 'compose' });
  const [accounts, setAccounts] = useState<string[]>([]);
  const [account, setAccount] = useState<string>('');
  const [years, setYears] = useState<number>(1);
  const [raw, setRaw] = useState<string>(initialDomains);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    let cancelled = false;
    APIClient.listRenewalAccounts()
      .then((list) => {
        if (cancelled) return;
        setAccounts(list);
        if (list.length === 1) setAccount(list[0]);
      })
      .catch(() => {
        if (!cancelled) setAccounts([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const domains = parseDomainList(raw);
  const duplicates = duplicateDomains(domains);
  const preview = state.kind === 'preview' || state.kind === 'starting' ? state.preview : null;

  const runPreview = async () => {
    setState({ kind: 'previewing' });
    try {
      const result = await APIClient.previewRenewal({ ncUsername: account, years, domains });
      setState({ kind: 'preview', preview: result });
      setConfirmText('');
    } catch (e: any) {
      setState({ kind: 'compose' });
      notification.error({ message: e?.response?.data?.error || 'Could not check these domains' });
    }
  };

  const start = async () => {
    if (!preview) return;
    setState({ kind: 'starting', preview });
    try {
      const { jobId } = await APIClient.startRenewal({
        ncUsername: account,
        years,
        domains,
        confirm: RENEWAL_CONFIRM_WORD,
      });
      onStarted(jobId);
    } catch (e: any) {
      // The server rebuilds the preview when a run starts, so a refusal here
      // means what it costs or what is renewable moved since it was read.
      const fresh = e?.response?.data?.preview as RenewalPreview | undefined;
      if (fresh) {
        setState({ kind: 'preview', preview: fresh });
        setConfirmText('');
        notification.warning({
          message: 'These domains changed since you checked them',
          description: fresh.blockedReason || 'Read the list again before starting.',
        });
        return;
      }
      setState({ kind: 'preview', preview });
      notification.error({ message: e?.response?.data?.error || 'Could not start the renewal' });
    }
  };

  const columns: Column<RenewalPreview['items'][number]>[] = [
    { key: 'domain', title: 'Domain', render: (row) => row.domain },
    {
      key: 'status',
      title: 'Status',
      render: (row) =>
        row.eligible ? (
          <span className="flex gap-1 items-center">
            <StatusTag tone={actionTone(row)}>{actionLabel(row) ?? 'Will renew'}</StatusTag>
            {row.isPremium && <StatusTag tone="accent">Premium</StatusTag>}
          </span>
        ) : (
          <StatusTag tone="neutral">Excluded</StatusTag>
        ),
    },
    {
      key: 'expires',
      title: 'Expires',
      render: (row) => (row.expiresAt ? moment(row.expiresAt).format('YYYY-MM-DD') : '—'),
    },
    {
      key: 'newExpiry',
      title: 'Becomes',
      render: (row) => {
        if (!row.eligible) return '—';
        const estimated = estimatedNewExpiry(row.expiresAt, preview?.years ?? 0, row.action);
        return estimated ? moment(estimated).format('YYYY-MM-DD') : '—';
      },
    },
    { key: 'reason', title: 'Reason', render: (row) => row.reason || row.warning || '' },
    {
      key: 'price',
      title: 'Est. price',
      align: 'right',
      render: (row) => (row.eligible ? formatCents(row.estimatedPriceCents, preview?.currency || '') : '—'),
    },
  ];

  const busy = state.kind === 'previewing' || state.kind === 'starting';

  return (
    <Modal
      open
      title="Bulk Renew Domains"
      width={840}
      onCancel={onClose}
      maskClosable={!busy}
      footer={null}
    >
      {!preview && (
        <div className="flex flex-col gap-3">
          {accounts.length === 0 && (
            <Callout tone="warn">
              No Namecheap accounts have API credentials configured, so nothing can be renewed here yet.
            </Callout>
          )}
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="text-[12px] text-text-3 mb-1">Namecheap account</div>
              <Select
                className="w-full"
                value={account || undefined}
                placeholder="Select an account"
                onChange={setAccount}
                options={accounts.map((a) => ({ label: a, value: a }))}
              />
            </div>
            <div style={{ width: 140 }}>
              <div className="text-[12px] text-text-3 mb-1">Renew for</div>
              <Select
                className="w-full"
                value={years}
                onChange={setYears}
                options={RENEWAL_YEAR_OPTIONS.map((y) => ({ label: `${y} year${y > 1 ? 's' : ''}`, value: y }))}
              />
            </div>
          </div>

          <div>
            <div className="text-[12px] text-text-3 mb-1">Domains, one per line</div>
            <Input.TextArea
              rows={10}
              value={raw}
              onChange={(e) => setRaw(e.target.value)}
              placeholder={'example.com\nanother.com'}
            />
            <div className="text-[12px] text-text-3 mt-1">
              {domains.length} domain{domains.length === 1 ? '' : 's'}
              {duplicates.length > 0 && `, ${duplicates.length} repeated`}
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={runPreview} disabled={!account || domains.length === 0 || busy}>
              {state.kind === 'previewing' ? 'Checking…' : 'Check these domains'}
            </Button>
          </div>
        </div>
      )}

      {preview && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-6 text-[13px]">
            <div>
              <div className="text-text-3">Will renew</div>
              <div>{preview.eligibleCount}</div>
            </div>
            <div>
              <div className="text-text-3">Excluded</div>
              <div>{preview.excludedCount}</div>
            </div>
            <div>
              <div className="text-text-3">Estimated total</div>
              <div>{formatCents(preview.estimatedTotalCents, preview.currency)}</div>
            </div>
            <div>
              <div className="text-text-3">Account balance</div>
              <div>{formatCents(preview.availableBalanceCents, preview.currency)}</div>
            </div>
          </div>

          <Callout tone="info">{pricingBasisSentence(preview)}</Callout>

          {preview.reactivateCount > 0 && (
            <Callout tone="warn">
              {preview.reactivateCount === 1 ? 'One domain has' : `${preview.reactivateCount} domains have`} already
              expired and {preview.reactivateCount === 1 ? 'has' : 'have'} to be restored rather than renewed, at the
              registrar's restore fee. {preview.reactivateCount === 1 ? 'Its price below is' : 'Their prices below are'}{' '}
              that fee, not a renewal.
            </Callout>
          )}

          {excludedPremiumSentence(preview) && <Callout tone="warn">{excludedPremiumSentence(preview)}</Callout>}

          {!preview.canProceed && (
            <Callout tone="danger">
              {preview.blockedReason}
              {shortfallCents(preview) > 0 &&
                ` Short by ${formatCents(shortfallCents(preview), preview.currency)}.`}
            </Callout>
          )}

          <DataTable
            columns={columns}
            data={preview.items}
            rowKey={(row) => row.domain}
            emptyMessage="No domains to show."
          />

          <div>
            <div className="text-[12px] text-text-3 mb-1">
              Type <span className="font-medium">{RENEWAL_CONFIRM_WORD}</span> to run {compositionSentence(preview)}{' '}
              for {formatCents(preview.estimatedTotalCents, preview.currency)}
            </div>
            <Input
              value={confirmText}
              disabled={!preview.canProceed}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={RENEWAL_CONFIRM_WORD}
              aria-label="Type confirm to start the renewal"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setState({ kind: 'compose' })} disabled={busy}>
              Back
            </Button>
            <Button onClick={start} disabled={!canStartRenewal(preview, confirmText) || busy}>
              {state.kind === 'starting' ? 'Starting…' : 'Start renewal'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default BulkRenewalModal;
